import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import {
  StyledTableContainer,
  StyledTable,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeaderCell,
  StyledHeaderContent,
  StyledSortIcon,
} from './Table/Table.styled';

// Table component variants using CSS variables
const tableVariants = {
  default: 'default',
  bordered: 'bordered',
  striped: 'striped',
} as const;

const tableSizes = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
} as const;

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  variant?: keyof typeof tableVariants;
  size?: keyof typeof tableSizes;
  children: React.ReactNode;
  cols?: number;
  hasCheckbox?: boolean;
  columnWidths?: (string | number | undefined)[];
}

export interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  appearance?: 'default' | 'minimal' | 'ghost';
  children: React.ReactNode;
}

export interface TableBodyProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export interface TableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  variant?: 'default' | 'active' | 'hover';
  appearance?: 'default' | 'minimal' | 'ghost';
  children: React.ReactNode;
}

export interface TableCellProps
  extends React.TdHTMLAttributes<HTMLTableDataCellElement> {
  variant?: 'default' | 'header' | 'accent';
  align?: 'left' | 'center' | 'right';
  appearance?: 'default' | 'minimal' | 'ghost';
  children: React.ReactNode;
}

export interface TableHeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableHeaderCellElement> {
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
  appearance?: 'default' | 'minimal' | 'ghost';
  align?: 'left' | 'center' | 'right';
  children: React.ReactNode;
}

// Main Table Component
export const Table: React.FC<TableProps> = ({
  children,
  className,
  variant = 'default',
  size = 'md',
  cols = 5,
  hasCheckbox = false,
  columnWidths,
  ...props
}) => {
  return (
    <StyledTableContainer className={className}>
      <StyledTable $variant={variant} $size={size} $cols={cols} $hasCheckbox={hasCheckbox} {...props}>
        <colgroup>
          {Array.from({ length: cols }).map((_, index) => {
            const width = columnWidths?.[index];
            const style = width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined;
            return <col key={index} style={style} />;
          })}
        </colgroup>
        {children}
      </StyledTable>
    </StyledTableContainer>
  );
};

// Table Header Component
export const TableHeader: React.FC<TableHeaderProps> = ({
  appearance = 'default',
  className,
  children,
  ...props
}) => {
  return (
    <StyledTableHeader
      $appearance={appearance}
      className={className}
      {...props}
    >
      {children}
    </StyledTableHeader>
  );
};

// Table Body Component
export const TableBody: React.FC<TableBodyProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <StyledTableBody className={className} {...props}>
      {children}
    </StyledTableBody>
  );
};

// Table Row Component
export const TableRow: React.FC<TableRowProps> = ({
  variant = 'default',
  appearance = 'default',
  className,
  children,
  ...props
}) => {
  return (
    <StyledTableRow
      $variant={variant}
      $appearance={appearance}
      className={className}
      {...props}
    >
      {children}
    </StyledTableRow>
  );
};

// Table Cell Component
export const TableCell: React.FC<TableCellProps> = ({
  variant = 'default',
  align = 'left',
  appearance = 'default',
  className,
  children,
  ...props
}) => {
  return (
    <StyledTableCell
      $variant={variant}
      $align={align}
      $appearance={appearance}
      className={className}
      {...props}
    >
      {children}
    </StyledTableCell>
  );
};

// Table Header Cell Component with Sorting
export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  sortable = false,
  sortDirection = null,
  onSort,
  align = 'left',
  className,
  children,
  appearance = 'default',
  ...props
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (sortable && onSort && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onSort();
    }
  };

  return (
    <StyledTableHeaderCell
      $sortable={sortable}
      $align={align}
      $appearance={appearance}
      onClick={sortable ? onSort : undefined}
      onKeyDown={sortable ? handleKeyDown : undefined}
      role={sortable ? 'button' : undefined}
      tabIndex={sortable ? 0 : undefined}
      className={className}
      {...props}
    >
      {sortable ? (
        <StyledHeaderContent>
          <span>{children}</span>
          <StyledSortIcon $direction={sortDirection}>
            <div className="sort-arrows">
              <ChevronUp
                size={14}
                className={`arrow-up ${
                  sortDirection === 'asc' ? 'active' : ''
                }`}
                strokeWidth={2}
              />
              <ChevronDown
                size={14}
                className={`arrow-down ${
                  sortDirection === 'desc' ? 'active' : ''
                }`}
                strokeWidth={2}
              />
            </div>
          </StyledSortIcon>
        </StyledHeaderContent>
      ) : (
        children
      )}
    </StyledTableHeaderCell>
  );
};
